/* ═══════════════════════════════════════════════════════════════
   ModuleTagsRail — "see every tag in use, click to filter" section of
   `ModuleBrowser`, below the category entries in BOTH layouts (migration
   056 — `module_library.tags`). Pure display + click-to-toggle; every
   count and the AND-narrowing itself come from `moduleTagFilter.ts`, the
   selection itself is owned by `useModuleCategoryBrowser`.

   `rail` (library page): expanded by default, no vertical budget
   pressure (the whole left column already scrolls).
   `stacked` (Spec Studio panel): collapsed by default — the panel's
   table is the thing that must stay visible — behind a caret the user
   opens, remembered per host the same way the browser remembers its
   last category (`Prefs`).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { ChevronDown, ChevronRight, Check, Pencil, Search } from 'lucide-react';
import { Icon } from './ui/Icon';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { rem } from '../lib/rem';
import { Prefs } from '../services/prefsService';
import {
  orderTagChips,
  filterTagChips,
  splitTagChips,
  type TagCount,
} from '../lib/moduleTagFilter';
import { ModuleTagsManager } from './ModuleTagsManager';

export interface ModuleTagsRailProps {
  /** Scopes the remembered expanded/collapsed state — same id the host
   *  passes to `ModuleBrowser` as `hostId`. */
  hostId: string;
  layout: 'rail' | 'stacked';
  tags: TagCount[];
  /** Catalog tags no module carries — offered after used chips at count 0. */
  catalogTags?: string[];
  selected: string[];
  onToggle: (tag: string) => void;
  onClear: () => void;
  /** Host's current user, permissions and toast — the Manage tags dialog writes. */
  userName: string;
  userPermissions: string[];
  toast: (msg: string, isError?: boolean) => void;
  /** A pick surface: chips filter, but the Manage tags pencil is hidden. */
  readOnly?: boolean;
}

export function ModuleTagsRail({
  hostId,
  layout,
  tags,
  catalogTags,
  selected,
  onToggle,
  onClear,
  userName,
  userPermissions,
  toast,
  readOnly = false,
}: ModuleTagsRailProps) {
  const [managing, setManaging] = React.useState(false);
  const canManage = !readOnly && userPermissions.includes('modules.edit');
  const prefKey = `module-browser.tagsExpanded.${hostId}`;
  const [expanded, setExpanded] = React.useState<boolean>(() => {
    const saved = Prefs.get(prefKey);
    return typeof saved === 'boolean' ? saved : layout === 'rail';
  });
  const [showAllChips, setShowAllChips] = React.useState(false);

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      Prefs.set(prefKey, next);
      return next;
    });
  };

  const selectedSet = new Set(selected.map((t) => t.toLowerCase()));
  const [query, setQuery] = React.useState('');
  const ordered = React.useMemo(
    () => orderTagChips(tags, selected, catalogTags),
    [tags, selected, catalogTags]
  );
  const searching = query.trim().length > 0;
  const matches = filterTagChips(ordered, query);
  const { visible, hiddenCount } = splitTagChips(matches, showAllChips || searching);
  const scrolling = searching || showAllChips;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <button
          type="button"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse tags' : 'Expand tags'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: rem(4),
            minHeight: rem(32),
            background: 'none',
            border: 'none',
            padding: `0 ${rem(4)}`,
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: 'var(--tx)',
          }}
        >
          <Icon
            icon={expanded ? ChevronDown : ChevronRight}
            size="xs"
            aria-hidden="true"
            style={{ color: 'var(--tm)' }}
          />
          Tags
          <span style={{ fontWeight: 400, color: 'var(--tm)' }}>{ordered.length}</span>
        </button>
        {canManage && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Manage tags"
            title="Manage tags"
            onClick={() => setManaging(true)}
          >
            <Icon icon={Pencil} size="sm" aria-hidden="true" />
          </Button>
        )}
        <div style={{ flex: 1 }} />
        {selected.length > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>

      {expanded && (
        <>
          <div style={{ position: 'relative' }}>
            <Icon
              icon={Search}
              size="xs"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: rem(10),
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--tm)',
                pointerEvents: 'none',
              }}
            />
            <Input
              type="text"
              value={query}
              placeholder="Search tags"
              aria-label="Search tags"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && query) {
                  e.stopPropagation();
                  setQuery('');
                }
              }}
              style={{ paddingLeft: rem(30) }}
            />
          </div>
          {ordered.length === 0 ? (
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', margin: 0 }}>
              No tags yet
            </p>
          ) : matches.length === 0 ? (
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)', margin: 0 }}>
              No tags match
            </p>
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-1)',
                ...(scrolling ? { maxHeight: rem(256), overflowY: 'auto' as const } : {}),
              }}
            >
              {visible.map(({ tag, count }) => {
                const isSelected = selectedSet.has(tag.toLowerCase());
                return (
                  <button
                    key={tag}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onToggle(tag)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: rem(4),
                      minHeight: rem(32),
                      padding: `0 ${rem(10)}`,
                      borderRadius: 'var(--radius-full)',
                      border: isSelected ? '1px solid var(--ac)' : '1px solid var(--bd)',
                      background: isSelected ? 'rgba(var(--acr), 0.14)' : 'var(--sa)',
                      color: isSelected ? 'var(--ac)' : 'var(--tm)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: isSelected ? 700 : 400,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isSelected && <Icon icon={Check} size="xs" aria-hidden="true" />}
                    {tag} · {count}
                  </button>
                );
              })}
            </div>
          )}
          {!searching && (hiddenCount > 0 || showAllChips) && matches.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAllChips((v) => !v)}
              style={{
                alignSelf: 'flex-start',
                background: 'none',
                border: 'none',
                padding: `0 ${rem(4)}`,
                minHeight: rem(32),
                color: 'var(--ac)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {showAllChips ? 'Show less' : `Show all ${matches.length}`}
            </button>
          )}
        </>
      )}
      {managing && (
        <ModuleTagsManager
          open
          onOpenChange={setManaging}
          userName={userName}
          userPermissions={userPermissions}
          toast={toast}
        />
      )}
    </div>
  );
}

export default ModuleTagsRail;
